import { getSupabase } from '@/lib/supabase'
import { withAuthRecovery } from '@/lib/authRecovery'

export type TeacherInviteOption = {
  teacher_id: string
  full_name: string
  teacher_status: string
  teacher_email: string | null
  created_source: 'official' | 'franchisee'
  needs_pedagogy_review: boolean
  linked_user_id: string | null
  assigned_to_partner: boolean
}

export type PartnerTeacherEmailMatch = {
  teacher_id: string
  full_name: string
  phone_number: string | null
  email: string
  telegram_username: string | null
  teacher_status: string
  has_current_link_to_partner: boolean
}

export type PartnerTeacherAppState = {
  teacher_id: string
  full_name: string
  teacher_status: string
  created_source: 'official' | 'franchisee'
  needs_pedagogy_review: boolean
  user_id: string | null
  invite_id: string | null
  invite_token: string | null
  invite_email: string | null
  invite_expires_at: string | null
  request_id: string | null
  request_status: 'pending' | 'approved' | 'rejected' | 'cancelled' | 'revoked' | null
  request_revoked_by_scope: 'franchisee' | 'management' | 'unknown' | null
  teacher_email: string | null
  has_active_team_membership: boolean
}

export type TeacherRoleRequest = {
  id: string
  teacher_id: string
  teacher_name: string
  teacher_status: string
  partner_number: number
  partner_name: string | null
  requested_by: string
  requested_at: string
  status: 'pending' | 'approved' | 'rejected' | 'cancelled' | 'revoked'
  reviewed_by: string | null
  reviewed_at: string | null
  review_comment: string | null
  cancelled_at: string | null
  revoked_at: string | null
}

const rpc = async <T>(name: string, args?: Record<string, unknown>): Promise<T> => {
  const client = getSupabase()
  const { data, error } = await withAuthRecovery(() => client.rpc(name as never, args as never)) as {
    data: T | null
    error: { message?: string } | null
  }
  if (error) throw new Error(error.message || `RPC ${name} failed`)
  return data as T
}

export const listPartnerTeacherInviteOptions = (partnerNumber: number) =>
  rpc<TeacherInviteOption[]>('list_partner_teacher_invite_options', {
    p_partner_number: partnerNumber,
  })

export const findPartnerTeacherByEmail = (partnerNumber: number, email: string) =>
  rpc<PartnerTeacherEmailMatch[]>('find_partner_teacher_by_email', {
    p_partner_number: partnerNumber,
    p_email: email.trim().toLowerCase(),
  })

export const listPartnerTeacherAppStates = (partnerNumber: number) =>
  rpc<PartnerTeacherAppState[]>('list_partner_teacher_app_states', {
    p_partner_number: partnerNumber,
  })

export const listPartnerTeacherAssignmentCityIds = (teacherId: string, partnerNumber: number) =>
  rpc<number[]>('list_partner_teacher_assignment_city_ids', {
    p_teacher_id: teacherId,
    p_partner_number: partnerNumber,
  })

export const listTeacherRoleRequestsForManagement = (status?: TeacherRoleRequest['status']) =>
  rpc<TeacherRoleRequest[]>('list_teacher_role_requests_for_management', {
    p_status: status || null,
  })

export const createTeacherInvite = (input: {
  partnerNumber: number
  teacherId: string | null
  firstName: string
  lastName: string
  email: string
  cityIds: number[]
}) => rpc<{ inviteId: string; token: string; teacherId: string; targetEmail: string; expiresAt: string }>(
  'create_teacher_invite',
  {
    p_partner_number: input.partnerNumber,
    p_teacher_id: input.teacherId,
    p_first_name: input.firstName,
    p_last_name: input.lastName,
    p_email: input.email.trim().toLowerCase(),
    p_city_ids: input.cityIds,
  },
)

export const confirmTeacherRegistryCard = (teacherId: string) =>
  rpc<void>('confirm_franchise_teacher_registry_card', { p_teacher_id: teacherId })

export const confirmTeacherFranchiseAssignment = (assignmentId: string) =>
  rpc<void>('confirm_teacher_franchise_assignment', { p_assignment_id: assignmentId })

export const createTeacherRoleRequest = (teacherId: string, partnerNumber: number) =>
  rpc<TeacherRoleRequest>('create_teacher_role_request', {
    p_teacher_id: teacherId,
    p_partner_number: partnerNumber,
  })

export const cancelTeacherRoleRequest = (requestId: string) =>
  rpc<void>('cancel_teacher_role_request', { p_request_id: requestId })

export const reviewTeacherRoleRequest = (
  requestId: string,
  decision: 'approved' | 'rejected',
  comment?: string,
) => rpc<TeacherRoleRequest>('review_teacher_role_request', {
  p_request_id: requestId,
  p_decision: decision,
  p_comment: comment || null,
})

export const revokeTeacherRoleRequest = (requestId: string) =>
  rpc<void>('revoke_teacher_role_request', { p_request_id: requestId })

export const restoreTeacherRoleAccess = (requestId: string) =>
  rpc<TeacherRoleRequest>('restore_teacher_role_access', { p_request_id: requestId })

export const reopenTeacherRoleRequest = (requestId: string) =>
  rpc<TeacherRoleRequest>('reopen_teacher_role_request', { p_request_id: requestId })

export const revokeTeacherInvite = (inviteId: string) =>
  rpc<void>('revoke_teacher_invite', { p_invite_id: inviteId })

export const createTeacherReinvite = (teacherId: string, partnerNumber: number, cityIds: number[]) =>
  rpc<{ inviteId: string; token: string; targetEmail: string }>('create_teacher_reinvite', {
    p_teacher_id: teacherId,
    p_partner_number: partnerNumber,
    p_city_ids: cityIds,
  })
